import { useContext } from 'react'

import { Form } from '@f/elements/HookForm'
import yup from '@f/elements/HookForm/validator/yup'

import { QuizContext } from '../../context/QuizContext'
import QuestionHeading from '../../partials/QuestionHeading'
import { QuizStep } from '../../partials/types'
import { SliderElement } from './components'

type LandAndAreaSchema = {
  property_living_area: number
  property_land_area: number
}

interface SliderElementProps {
  name: string
  label: string
  min: number
  max: number
  step: number
  default: number
  valueSuffix: string
  valuePrefix: string
  stepId: string
}

interface StepConfig {
  heading: string
  subheading: string
  firstSlider: SliderElementProps
  secondSlider?: SliderElementProps
}

export const QuestionLivingAndLandArea = ({ step }: { step: QuizStep }) => {
  const context = useContext(QuizContext)

  const propertyType = context?.data?.steps?.find(
    (s: QuizStep) => s.id === 'property_type'
  )?.values?.[0]?.value

  const stepConfigForHouseVariant: StepConfig = {
    heading: 'Wie groß ist die Wohn- und Grundstückfläche Ihrer Immobilie?',
    subheading: '',
    firstSlider: {
      name: 'property_living_area',
      min: 50,
      default: 140,
      max: 300,
      step: 5,
      valuePrefix: '',
      valueSuffix: 'm²',
      label: 'Wohnfläche Ihres Hauses',
      stepId: step.id
    },
    secondSlider: {
      name: 'property_land_area',
      min: 200,
      default: 500,
      max: 2000,
      valuePrefix: '',
      valueSuffix: 'm²',
      step: 25,
      stepId: step.id,
      label: 'Fläche Ihres Grundstücks'
    }
  }

  const stepConfigForApartmentVariant: StepConfig = {
    heading: 'Wie groß ist die Wohnfläche Ihrer Immobilie?',
    subheading: '',
    firstSlider: {
      name: 'property_living_area',
      min: 20,
      default: 80,
      max: 300,
      step: 5,
      valuePrefix: '',
      valueSuffix: 'm²',
      label: 'Ein ungefährer Wert ist in Ordnung',
      stepId: step.id
    }
  }

  const stepConfig =
    propertyType === 'house'
      ? stepConfigForHouseVariant
      : stepConfigForApartmentVariant

  const defaultValues = {
    property_living_area: stepConfig.firstSlider.default,
    property_land_area: stepConfig.secondSlider?.default
  }

  const livingAndLandAreaSchema: yup.ObjectSchema<LandAndAreaSchema> = yup.object(
    {
      property_living_area: yup
        .number()
        .required()
        .min(stepConfig.firstSlider.min)
        .max(stepConfig.firstSlider.max),
      property_land_area: yup
        .number()
        .optional()
        .min(stepConfig?.secondSlider?.min)
        .max(stepConfig?.secondSlider?.max)
    }
  )

  return (
    <>
      <QuestionHeading
        heading={stepConfig.heading}
        subheading={step.subheading}
      />
      <div className='[ f-wrap ]'>
        <Form.Wrapper
          onSubmit={() => {
            // Submission happens in SliderElement
          }}
          defaultValues={defaultValues}
          validationSchema={livingAndLandAreaSchema}>
          <SliderElement
            stepId={step.id}
            name={stepConfig.firstSlider.name}
            label={stepConfig.firstSlider.label}
            minValue={stepConfig.firstSlider.min}
            maxValue={stepConfig.firstSlider.max}
            valueStep={stepConfig.firstSlider.step}
            valueSuffix='m²'
            valuePrefix=''
          />
          {stepConfig.secondSlider && (
            <SliderElement
              stepId={step.id}
              name={stepConfig.secondSlider.name}
              label={stepConfig.secondSlider.label}
              minValue={stepConfig.secondSlider.min}
              maxValue={stepConfig.secondSlider.max}
              valueStep={stepConfig.secondSlider.step}
              valueSuffix='m²'
              valuePrefix=''
            />
          )}
        </Form.Wrapper>
      </div>
    </>
  )
}
